/**
 * A failed call to the control API. The simulator answers most errors with a status and no body (400 for an invalid
 * value, 409 for a conflict); a few carry a JSON body, kept in `body` (`{"error":"capacity"}` on a 503,
 * `{"error":"too-many-chargers","limit":n}` on a 400). `status` is 0 when the request never got a response
 * (network error, aborted).
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  /** The parsed JSON body of the answer; `undefined` when there was none or it was not JSON. */
  readonly body: unknown;

  constructor(status: number, message: string, options?: { cause?: unknown; body?: unknown }) {
    super(message, options);
    this.status = status;
    this.body = options?.body;
  }

  /** The body's `error` code (`capacity`, `too-many-chargers`), when the answer had one. */
  get code(): string | undefined {
    const code = isRecord(this.body) ? this.body.error : undefined;
    return typeof code === 'string' ? code : undefined;
  }

  /** The body's `limit`, when the answer had one. */
  get limit(): number | undefined {
    const limit = isRecord(this.body) ? this.body.limit : undefined;
    return typeof limit === 'number' ? limit : undefined;
  }

  /** Builds the error from a failed response. */
  static fromResponse(response: Response, body?: unknown): ApiError {
    return new ApiError(
      response.status,
      `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`,
      { body },
    );
  }

  static network(cause: unknown): ApiError {
    return new ApiError(0, cause instanceof Error ? cause.message : 'Network error', { cause });
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Unwraps an openapi-fetch result: the data on success, an {@link ApiError} otherwise.
 *
 *   const status = await unwrap(client.GET('/simulation/status'));
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch (error) {
    throw ApiError.network(error);
  }
  if (!result.response.ok) throw ApiError.fromResponse(result.response, result.error);
  return result.data as T;
}
